/**
 * Plain-English glossary panel.
 *
 * Collapsed by default; the single user can open it once and they're
 * unlikely to need it again. Lives at the top of the Singles/Doubles tab so
 * it's reachable without having to scroll.
 */
export function HowToRead() {
  return (
    <details className="panel p-3 text-sm text-ink-700">
      <summary className="cursor-pointer select-none text-xs font-semibold uppercase tracking-wide text-ink-500">
        How to read this
      </summary>

      <div className="mt-3 space-y-3">
        <p>
          <strong className="text-ink-900">If you read nothing else:</strong> the top panel (&ldquo;Bet on 5–10 of
          these games&rdquo;) is the answer. Each row has a one-word verdict in plain English:{" "}
          <strong className="text-emerald-700">★ BEST</strong> means bet on it,{" "}
          <strong className="text-emerald-700">👍 GOOD</strong> means consider it, <strong>✓ OK</strong> means
          it&apos;s a popular favourite with no real edge, <strong className="text-rose-700">⚠ AVOID</strong>{" "}
          means skip. The big number on the right is your bottom-line — green = good, red = bad.
        </p>
        <p>
          <strong className="text-ink-900">What this app does.</strong> It pulls today&apos;s football odds
          from Norsk Tipping, strips out the bookmaker&apos;s profit margin, and ranks the picks most likely
          to win — only from leagues where pricing is sharp enough to trust.
        </p>

        <Block title="The columns on each pick">
          <Term name="Odds">
            What you&apos;d be paid out in decimal format. <em>2.00</em> means €1 → €2 back (€1 profit).
            Higher number = bookie thinks it&apos;s less likely.
          </Term>
          <Term name="Fair">
            Same odds but with the bookmaker&apos;s profit removed. The further <em>Fair</em> is below{" "}
            <em>Odds</em>, the more margin the book is taking on this market.
          </Term>
          <Term name="Prob">
            Our estimate of the true chance this pick wins — derived from the bookmaker&apos;s prices with
            their margin scrubbed off. <em>60%</em> = we think it happens 6 times in 10.
          </Term>
          <Term name="EV">
            Expected value per €1 staked, compared to a consensus of sharp bookmakers. Positive (
            <span className="font-semibold text-emerald-700">green</span>) = the bet looks mispriced in your
            favor. Negative (<span className="font-semibold text-rose-700">red</span>) = you&apos;re paying
            the house. Most NT bets sit slightly negative.
          </Term>
          <Term name="Stake (doubles only)">
            A suggested portion of your bankroll to risk, capped well below full Kelly because our probability
            estimate is itself uncertain. A dash (<em>—</em>) means the bet isn&apos;t worth placing.
          </Term>
        </Block>

        <Block title="The verdict (the most important chip)">
          <Term name="★ BEST">
            Our model AND the sharp bookies both say this is mispriced in your favor, and their numbers agree
            closely. Backtested at <strong>+5.2% ROI</strong> over 5 seasons — the only category that
            historically made money. Rare (a few per week, often zero).
          </Term>
          <Term name="👍 GOOD">
            One of the two signals (model or consensus) says +EV but not both. Worth considering but the
            evidence is weaker than BEST.
          </Term>
          <Term name="✓ OK">
            A reasonable favourite, but neither signal indicates an edge. Betting these long-term loses about
            the bookmaker&apos;s margin.
          </Term>
          <Term name="⚠ AVOID">Not a meaningful favourite (under 45% win chance). Skip.</Term>
        </Block>

        <Block title="Which number to look at">
          <Term name="Model EV">
            <strong>The best signal.</strong> Expected profit per €1 staked according to our independent
            model. The big number on a top pick if available. Positive = bet looks mispriced in your favor.
          </Term>
          <Term name="Consensus EV">
            Used as the big number when the model can&apos;t fire. Expected profit per €1 vs sharp-book
            consensus.
          </Term>
          <Term name="Probability">
            Fallback when neither EV is available. Just the chance the bet wins, no edge info.
          </Term>
        </Block>

        <Block title="The data-source chip (3/4, 2/4, …)">
          <Term name="3/4">
            How many of the four data sources contributed signal to this pick. The four are NT odds (always
            on), sharp consensus, our model, and weather forecast. Hover for which ones are missing and why.
            Higher = more confident.
          </Term>
        </Block>

        <Block title="The little chips next to each selection">
          <Term name="League badge (EPL, UCL, …)">
            Which competition the match is in. We only show picks from a curated list of trustworthy leagues.
          </Term>
          <Term name="vig badge">
            The bookmaker&apos;s mark-up on this 1X2 market. Green = ≤ 6% (sharp). Amber = 6–8% (acceptable).
            Above 8% the pick is filtered out.
          </Term>
          <Term name="EV badge">
            Expected value vs the sharp-book consensus. Positive = mispriced in your favor. Rare on NT.
          </Term>
          <Term name="MODEL badge">
            Expected value vs our <em>independent</em> Dixon-Coles model (it doesn&apos;t look at NT or other
            bookies — just historical goal rates × Elo × recent form). When this disagrees with the EV badge,
            one signal is wrong.
          </Term>
          <Term name="★ HIGH CONVICTION">
            The model badge AND the EV badge are both green, AND the two probability estimates agree within 4
            percentage points. Two independent signals pointing the same way = the rare configuration actually
            worth a stake.
          </Term>
          <Term name="weather chip">
            Forecast at kickoff (temperature + condition icon). Only shows when conditions are notable (heavy
            rain, strong wind, freezing). Tough weather tends to suppress goals and favour underdog draws.
          </Term>
        </Block>

        <Block title="The tabs">
          <Term name="Singles">
            One pick = one bet. Wins if the chosen outcome (home / draw / away) happens.
          </Term>
          <Term name="Doubles">
            Two picks combined into a single bet. Wins <em>only if both</em> happen. Bigger payout, smaller
            chance. <em>Safe</em> = highest joint probability. <em>Value</em> = best expected return per €1.
          </Term>
          <Term name="Backtest">
            How the algorithm would have actually performed on the last 5 seasons of historical results. Look
            here to see whether the picks make money.
          </Term>
        </Block>

        <Block title="The filters">
          <Term name="Leagues">
            Click a league chip to include / exclude it. By default all whitelisted leagues are on.
          </Term>
          <Term name="Max bookmaker margin">
            Hide picks from markets where the bookie&apos;s mark-up is above this. Lower = stricter quality.
          </Term>
          <Term name="Min fair probability">
            Hide picks below this estimated win chance. Raise it to see only strong favorites; lower it to see
            closer matches too.
          </Term>
        </Block>

        <Block title="Behind the scenes">
          <Term name="Sharp consensus">
            We pull live odds from Pinnacle / Betfair Exchange / Smarkets / Matchbook via the-odds-api. Those
            books are sharper than NT, so their average is a credible &quot;true price&quot;.
          </Term>
          <Term name="Dixon-Coles">
            Classic football probability model. Inputs: historical goal-scoring rates per league (from the
            football-data.co.uk archive), Elo strength per team (clubelo.com), and last-5 form
            (football-data.org). Output: 1X2 probability for any fixture. No NT odds in the loop.
          </Term>
          <Term name="Warming up">
            For competitions where we don&apos;t have Elo or form data, the model can&apos;t fire. Daily cron
            of API-Football xG will widen coverage over ~2 weeks.
          </Term>
        </Block>

        <p className="text-[11px] text-ink-500">
          Reality check: simple favorite-picking on NT data loses about 4% per €1 over the long run —
          that&apos;s just the bookmaker&apos;s margin. The Backtest tab shows it. The +EV badges (when
          consensus data is on) are the only way to actually beat the house.{" "}
          <strong>High-conviction picks</strong> are the strict subset where two independent signals agree the
          bet is mispriced — those are the ones worth staking.
        </p>
      </div>
    </details>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-500">{title}</p>
      <dl className="mt-1.5 space-y-1.5">{children}</dl>
    </div>
  );
}

function Term({ name, children }: { name: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-0.5">
      <dt className="text-xs font-semibold text-ink-900">{name}</dt>
      <dd className="text-xs leading-snug text-ink-700">{children}</dd>
    </div>
  );
}
